import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import {
  register_free,
  register_professional,
  register_team,
} from "../../url";
import { useCurrency } from "../../currency/CurrencyContext";
import RefSiteFooter from "./RefSiteFooter";
import RefPricingTeaser from "./RefPricingTeaser";

const PRICING_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rsp-ink: #163A5A; --rsp-ink2: #3D6A8C; --rsp-ink3: #5E87A3;
    --rsp-sky: #1A63A5; --rsp-mint: #2AB6E8;
    --rsp-secondary: #2AB6E8;
    --rsp-secondary-deep: #1AA3D4;
    --rsp-secondary-soft: #D9F4FC;
    --rsp-secondary-muted: rgba(42,182,232,0.2);
    --rsp-sky-light: #E8F4FC;
    --rsp-sky-hover: #155a94;
    --rsp-bg-dark: #0E2D48;
    --rsp-white: #FFFFFF; --rsp-surface: #F5F9FC;
    --rsp-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif; --rsp-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }
  .rsp-page { font-family: var(--rsp-ff-body); color: var(--rsp-ink); background: var(--rsp-white); }

  /* HERO */
  .rsp-hero {
    text-align: center; padding: 72px 24px 56px;
    background: linear-gradient(180deg, #E8F4FC 0%, var(--rsp-secondary-soft) 35%, #fff 100%);
    position: relative; overflow: hidden;
  }
  .rsp-hero::before {
    content: ''; position: absolute; top: -120px; left: 50%; transform: translateX(-50%);
    width: 800px; height: 400px;
    background: radial-gradient(ellipse, rgba(26,99,165,.07) 0%, transparent 70%);
    pointer-events: none;
  }
  .rsp-h1 { font-family: var(--rsp-ff-head); font-size: clamp(36px, 5.5vw, 56px); font-weight: 800; letter-spacing: -.02em; line-height: 1.08; margin-bottom: 16px; }
  .rsp-h1-grad { background: linear-gradient(135deg, var(--rsp-sky), var(--rsp-mint)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .rsp-hero-sub { font-size: 17px; color: var(--rsp-ink2); font-weight: 400; max-width: 540px; margin: 0 auto 36px; line-height: 1.6; }

  /* TABS */
  .rsp-tabs { display: inline-flex; background: var(--rsp-surface); border: 1px solid #E0E3EF; border-radius: 12px; padding: 4px; gap: 4px; margin-bottom: 12px; }
  .rsp-tab { font-family: var(--rsp-ff-body); font-size: 14px; font-weight: 600; padding: 10px 28px; border-radius: 9px; border: 1px solid transparent; cursor: pointer; background: transparent; color: var(--rsp-ink3); transition: all .2s; }
  .rsp-tab.active { background: var(--rsp-white); color: var(--rsp-sky); border: 1px solid var(--rsp-secondary-muted); box-shadow: 0 2px 10px rgba(42,182,232,.18), inset 0 -2px 0 var(--rsp-secondary); }

  /* PLAN CARDS */
  .rsp-cards { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; max-width: 1400px; margin: 0 auto 32px; padding: 0 24px; }
  .rsp-card { 
    display: flex; flex-direction: column;
    border: 1px solid #E0E3EF; border-radius: 18px; overflow: hidden; transition: all .25s; position: relative; background: var(--rsp-white); 
  }
  .rsp-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(26,99,165,.1); border-color: rgba(26,99,165,.28); }
  .rsp-card.featured {
    border: 2px solid var(--rsp-secondary);
    box-shadow: 0 8px 32px rgba(42,182,232,.2), 0 0 0 1px rgba(26,99,165,.15);
  }
  .rsp-card-badge { position: absolute; top: -1px; left: 50%; transform: translateX(-50%); background: var(--rsp-sky); color: #fff; font-size: 11px; font-weight: 700; padding: 4px 14px; border-radius: 0 0 8px 8px; letter-spacing: .05em; white-space: nowrap; z-index: 2; }
  .rsp-card-top { padding: 36px 20px 24px; border-bottom: 1px solid #F0F2F8; min-height: 215px; display: flex; flex-direction: column; justify-content: flex-start; text-align: center; }
  .rsp-card-plan { font-family: var(--rsp-ff-head); font-size: 18px; font-weight: 700; color: var(--rsp-ink); margin-bottom: 4px; }
  .rsp-card-team { font-size: 13px; color: var(--rsp-ink3); margin-bottom: 20px; min-height: 20px; }
  .rsp-card-price {
    font-family: var(--rsp-ff-head); font-size: clamp(22px, 4vw, 34px); font-weight: 800;
    color: var(--rsp-sky); line-height: 1.2; margin-bottom: 6px;
    display: flex; flex-wrap: wrap; align-items: baseline; justify-content: center; column-gap: 6px;
    overflow-wrap: break-word; word-break: break-word; max-width: 100%;
  }
  .rsp-card-price span { font-size: 14px; font-weight: 400; color: var(--rsp-ink3); white-space: nowrap; }
  .rsp-card-price.contact { font-size: 22px; color: var(--rsp-sky); font-weight: 800; margin: 8px 0 12px; }
  .rsp-card-price-sub { font-size: 12px; color: var(--rsp-ink3); font-weight: 500; overflow-wrap: break-word; word-break: break-word; }
  .rsp-card-body { padding: 24px 20px; flex: 1; display: flex; flex-direction: column; }
  .rsp-feat-list { flex: 1; }
  .rsp-feat-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #F3F5FB; }
  .rsp-feat-row:last-child { border-bottom: none; }
  .rsp-feat-label { font-size: 13px; color: var(--rsp-ink2); text-align: left; }
  .rsp-feat-val { font-size: 13px; color: var(--rsp-ink); font-weight: 700; }
  .rsp-feat-val.unlimited { color: var(--rsp-sky); font-size: 11px; letter-spacing: 0.05em; font-weight: 800; }
  .rsp-feat-val.check { color: #16A34A; font-size: 16px; }
  .rsp-feat-val.cross { color: #DC2626; font-size: 16px; }
  .rsp-cta-btn { width: 100%; margin-top: 24px; font-family: var(--rsp-ff-body); font-size: 14.5px; font-weight: 600; padding: 13px; border-radius: 12px; cursor: pointer; transition: all .2s; border: none; }
  .rsp-cta-btn.fill { background: var(--rsp-sky); color: #fff; box-shadow: 0 4px 12px rgba(26,99,165,0.2); }
  .rsp-cta-btn.fill:hover { background: var(--rsp-sky-hover); transform: translateY(-1px); box-shadow: 0 6px 16px rgba(26,99,165,0.3); }
  .rsp-cta-btn.outline { background: transparent; border: 2px solid var(--rsp-sky); color: var(--rsp-sky); }
  .rsp-cta-btn.outline:hover { background: var(--rsp-sky-light); transform: translateY(-1px); }
  .rsp-card-actions { margin-top: auto; }
  .rsp-trial-tag { font-size: 11px; font-weight: 700; color: #16A34A; text-align: center; margin-top: 12px; letter-spacing: .02em; text-transform: uppercase; }

  /* NOTE */
  .rsp-note { max-width: 680px; margin: 0 auto 16px; padding: 16px 24px; background: var(--rsp-sky-light); border: 1px solid rgba(26,99,165,.2); border-radius: 12px; text-align: center; }
  .rsp-note p { font-size: 13.5px; color: var(--rsp-ink2); line-height: 1.6; margin: 0; }
  .rsp-note strong { color: var(--rsp-sky); }

  /* MONTHLY BANNER */
  .rsp-monthly-banner { 
    max-width: 720px; margin: 0 auto 24px; padding: 14px 24px; 
    background: #FFFBEB; border: 1px solid #FDE68A; border-radius: 12px; 
    display: flex; align-items: center; justify-content: center; gap: 12px;
    font-size: 14px; color: #92400E; font-weight: 500;
  }
  .rsp-monthly-banner .MuiSvgIcon-root { font-size: 20px; color: #B45309; }
  .rsp-monthly-banner strong { color: #B45309; font-weight: 700; }

  /* KBA */
  .rsp-kba { max-width: 860px; margin: 0 auto; padding: 20px 40px 64px; }
  .rsp-kba-intro { background: linear-gradient(135deg, rgba(26,99,165,.04), rgba(42,182,232,.04)); border: 1px solid rgba(26,99,165,.15); border-radius: 14px; padding: 24px 36px; margin-bottom: 24px; text-align: center; box-shadow: 0 4px 12px rgba(22,58,90,.03); }
  .rsp-kba-intro p { font-size: 16px; color: var(--rsp-ink2); line-height: 1.6; margin: 0; }
  .rsp-kba-intro strong { color: var(--rsp-ink); font-weight: 700; }
  .rsp-kba-table { width: 100%; border-collapse: collapse; border: 1px solid #E0E3EF; border-radius: 14px; overflow: hidden; }
  .rsp-kba-table thead { background: var(--rsp-bg-dark); }
  .rsp-kba-table thead th { padding: 16px 24px; text-align: left; font-family: var(--rsp-ff-head); font-size: 13px; font-weight: 600; color: rgba(255,255,255,.7); letter-spacing: .05em; }
  .rsp-kba-table thead th:last-child { text-align: right; }
  .rsp-kba-table tbody tr { border-bottom: 1px solid #EEF0F8; transition: background .15s; }
  .rsp-kba-table tbody tr:hover { background: var(--rsp-sky-light); }
  .rsp-kba-table tbody tr:last-child { border-bottom: none; }
  .rsp-kba-table tbody tr:nth-child(odd) { background: var(--rsp-surface); }
  .rsp-kba-table tbody tr:nth-child(odd):hover { background: var(--rsp-sky-light); }
  .rsp-kba-table td { padding: 14px 24px; font-size: 15px; }
  .rsp-kba-table td.range { color: var(--rsp-ink2); }
  .rsp-kba-table td.price { text-align: right; font-family: var(--rsp-ff-head); font-weight: 700; font-size: 20px; color: var(--rsp-sky); }
  .rsp-kba-table tr.best-rate td.price { color: var(--rsp-mint); }
  .rsp-kba-table tr.best-rate td.range .best-tag { font-size: 11px; color: var(--rsp-mint); font-weight: 600; margin-left: 8px; }

  @media (max-width: 1200px) {
    .rsp-cards { grid-template-columns: repeat(3, 1fr); padding: 0 24px; }
  }
  @media (max-width: 900px) {
    .rsp-cards { grid-template-columns: repeat(2, 1fr); padding: 0 24px; }
  }
  @media (max-width: 600px) {
    .rsp-cards { grid-template-columns: 1fr; }
    .rsp-h1 { font-size: 34px; }
    .rsp-hero { padding: 48px 20px 40px; }
    .rsp-tabs { flex-direction: column; width: 100%; }
    .rsp-kba { padding: 18px 20px 40px; }
    .rsp-kba-intro { padding: 20px 16px; }
  }
`;

const KBA_TIERS = [
  { range: "100 – 250", priceUsd: 2.49, best: false },
  { range: "251 – 500", priceUsd: 2.29, best: false },
  { range: "501 – 1,000", priceUsd: 1.99, best: false },
  { range: "1,001 – 5,000", priceUsd: 1.79, best: false },
  { range: "5,000+", priceUsd: 1.59, best: true },
];

function PricingAndPlans() {
  const { t } = useTranslation('pricing');
  const navigate = useNavigate();
  const { format } = useCurrency();
  const [activeTab, setActiveTab] = useState("annually");

  const pricingPlans = [
    {
      id: "free",
      plan: t("plans.individualFree.name"),
      teamSize: t("plans.individualFree.teamSize"),
      price: {
        annually: t("plans.individualFree.price"),
        monthly: t("plans.individualFree.price"),
      },
      priceUnit: { annually: "", monthly: "" },
      priceSub: {
        annually: t("plans.individualFree.sub"),
        monthly: t("plans.individualFree.sub"),
      },
      buttonLabel: t("actions.getStartedFree"),
      trialLabel: t("actions.noCardLabel"),
      documentsLabel: t("plans.individualFree.documents"),
      templatesLabel: t("plans.individualFree.templates"),
      publicFormsLabel: t("plans.individualFree.publicForms"),
      branding: false,
      customWebAddress: false,
      featured: false,
      isCustomPrice: false,
      registerUrl: register_free,
    },
    {
      id: "professional",
      plan: t("plans.individuals.name"),
      teamSize: t("plans.individuals.teamSize"),
      price: { annually: format(8.25), monthly: format(12) },
      priceUnit: { annually: "/mo/user", monthly: "/user" },
      priceSub: {
        annually: t("plans.individuals.subAnnually", { price: format(99) }),
        monthly: t("plans.individuals.subMonthly"),
      },
      buttonLabel: t("actions.getStarted"),
      trialLabel: t("actions.trialLabel"),
      branding: true,
      customWebAddress: false,
      featured: false,
      isCustomPrice: false,
      registerUrl: register_professional,
    },
    {
      id: "team",
      plan: t("plans.smallBusiness.name"),
      teamSize: t("plans.smallBusiness.teamSize"),
      price: { annually: format(7.42), monthly: format(11) },
      priceUnit: { annually: "/mo/user", monthly: "/user" },
      priceSub: {
        annually: t("plans.smallBusiness.subAnnually", { price: format(89) }),
        monthly: t("plans.smallBusiness.subMonthly"),
      },
      buttonLabel: t("actions.getStarted"),
      trialLabel: t("actions.trialLabel"),
      branding: true,
      customWebAddress: t("featureLabels.subdomain"),
      featured: true,
      isCustomPrice: false,
      registerUrl: register_team,
    },
    {
      id: "enterprise",
      plan: t("plans.enterprise.name"),
      teamSize: t("plans.enterprise.teamSize"),
      price: {
        annually: t("plans.enterprise.contactPrice"),
        monthly: t("plans.enterprise.contactPrice"),
      },
      priceUnit: { annually: "", monthly: "" },
      priceSub: {
        annually: t("plans.enterprise.sub"),
        monthly: t("plans.enterprise.sub"),
      },
      buttonLabel: t("actions.contactSales"),
      trialLabel: null,
      branding: true,
      customWebAddress: t("featureLabels.yourDomain"),
      featured: false,
      isCustomPrice: true,
    },
    {
      id: "onprem",
      plan: t("plans.onPrem.name"),
      teamSize: t("plans.onPrem.teamSize"),
      price: {
        annually: t("plans.onPrem.contactPrice"),
        monthly: t("plans.onPrem.contactPrice"),
      },
      priceUnit: { annually: "", monthly: "" },
      priceSub: {
        annually: t("plans.onPrem.sub"),
        monthly: t("plans.onPrem.sub"),
      },
      buttonLabel: t("actions.contactSales"),
      trialLabel: null,
      branding: true,
      customWebAddress: t("featureLabels.yourDomain"),
      featured: false,
      isCustomPrice: true,
    },
  ];

  const handleCTA = (plan) => {
    if (plan.registerUrl) {
      window.open(plan.registerUrl, "_blank");
      return;
    }
    if (plan.isCustomPrice) {
      navigate("/contact");
    }
  };

  return (
    <div className="rsp-page">
      <style>{PRICING_STYLES}</style>

      {/* HERO + TABS */}
      <div className="rsp-hero">
        <h1 className="rsp-h1">
          {t('hero.titlePrefix')}<span className="rsp-h1-grad">{t('hero.titleHighlight')}</span>{t('hero.titleSuffix')}
        </h1>
        <p className="rsp-hero-sub">
          {t('hero.subtitle')}
        </p>

        {/* Tab Bar */}
        <div className="rsp-tabs">
          {[
            ["annually", t('tabs.annually')],
            ["monthly", t('tabs.monthly')],
            ["kba", t('tabs.kba')]
          ].map(([key, label]) => (
            <button
              key={key}
              className={`rsp-tab${activeTab === key ? " active" : ""}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Plan Cards Annual & Monthly */}
        {activeTab !== "kba" && (
          <>
            {activeTab === "monthly" && (
              <div className="rsp-monthly-banner">
                <LightbulbOutlined />
                <span>
                  <strong>{t('monthlyBanner.proTip')}</strong> {t('monthlyBanner.saveText')}<strong>{t('monthlyBanner.savePercent')}</strong>{t('monthlyBanner.saveSuffix')}
                </span>
              </div>
            )}

            <div className="rsp-cards">
              {pricingPlans.map((plan) => {
                const price = plan.price[activeTab];
                const unit = plan.priceUnit[activeTab];
                const sub = plan.priceSub[activeTab];

                return (
                  <div className={`rsp-card${plan.featured ? " featured" : ""}`} key={plan.plan}>
                    {plan.featured && <div className="rsp-card-badge">{t('plans.mostPopular')}</div>}
                    <div className="rsp-card-top">
                      <div className="rsp-card-plan">{plan.plan}</div>
                      <div className="rsp-card-team">{plan.teamSize}</div>
                      <div className={`rsp-card-price${plan.isCustomPrice ? " contact" : ""}`}>
                        {price}
                        {!plan.isCustomPrice && unit && <span>{unit}</span>}
                      </div>
                      <div className="rsp-card-price-sub">{sub}</div>
                    </div>
                    <div className="rsp-card-body">
                      <div className="rsp-feat-list">
                        <div className="rsp-feat-row">
                          <span className="rsp-feat-label">{t('featureLabels.documents')}</span>
                          <span
                            className={`rsp-feat-val${
                              plan.documentsLabel ? "" : " unlimited"
                            }`}
                          >
                            {plan.documentsLabel ||
                              t("featureLabels.unlimited")}
                          </span>
                        </div>
                        <div className="rsp-feat-row">
                          <span className="rsp-feat-label">{t('featureLabels.templates')}</span>
                          <span
                            className={`rsp-feat-val${
                              plan.templatesLabel ? "" : " unlimited"
                            }`}
                          >
                            {plan.templatesLabel ||
                              t("featureLabels.unlimited")}
                          </span>
                        </div>
                        <div className="rsp-feat-row">
                          <span className="rsp-feat-label">{t('featureLabels.publicForms')}</span>
                          <span
                            className={`rsp-feat-val${
                              plan.publicFormsLabel ? "" : " unlimited"
                            }`}
                          >
                            {plan.publicFormsLabel ||
                              t("featureLabels.unlimited")}
                          </span>
                        </div>
                        <div className="rsp-feat-row">
                          <span className="rsp-feat-label">{t('featureLabels.branding')}</span>
                          {plan.branding ? (
                            <span className="rsp-feat-val check">✓</span>
                          ) : (
                            <span className="rsp-feat-val cross">✗</span>
                          )}
                        </div>
                        <div className="rsp-feat-row">
                          <span className="rsp-feat-label">{t('featureLabels.customWebAddress')}</span>
                          {plan.customWebAddress
                            ? <span className="rsp-feat-val">{plan.customWebAddress}</span>
                            : <span className="rsp-feat-val cross">✗</span>
                          }
                        </div>
                      </div>
                      <div className="rsp-card-actions">
                        <button
                          className={`rsp-cta-btn ${!plan.isCustomPrice ? "fill" : "outline"}`}
                          onClick={() => handleCTA(plan)}
                        >
                          {plan.buttonLabel}
                        </button>
                        {plan.trialLabel ? (
                          <div className="rsp-trial-tag">{plan.trialLabel}</div>
                        ) : (
                          <div className="rsp-trial-tag" style={{ visibility: "hidden" }}>Placeholder</div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="rsp-note">
              <p>
                {t('note.prefix')}<strong>{t('note.highlight')}</strong>{t('note.suffix')}
              </p>
            </div>
          </>
        )}

        {/* KBA Tab */}
        {activeTab === "kba" && (
          <div className="rsp-kba">
            <div className="rsp-kba-intro">
              <p>
                {t('kba.introPrefix')}<strong>{t('kba.accountant')}</strong>{t('kba.introMid')}<strong>{t('kba.kbaTerm')}</strong>{t('kba.introMid2')}<strong>{t('kba.addon')}</strong>{t('kba.introMid3')}<br />
                <strong>{t('kba.kbaTerm')}</strong>{t('kba.irsNotePrefix')}<strong>{t('kba.irs')}</strong>{t('kba.irsNoteSuffix')}
              </p>
            </div>
            <table className="rsp-kba-table">
              <thead>
                <tr>
                  <th>{t('kba.table.credits')}</th>
                  <th style={{ textAlign: "right" }}>{t('kba.table.pricePerKba')}</th>
                </tr>
              </thead>
              <tbody>
                {KBA_TIERS.map(({ range, priceUsd, best }) => (
                  <tr className={best ? "best-rate" : ""} key={range}>
                    <td className="range">
                      {range}
                      {best && <span className="best-tag">• {t('kba.table.bestRate')}</span>}
                    </td>
                    <td className="price">{format(priceUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PRICING TEASER & FOOTER */}
      <RefPricingTeaser />
      <RefSiteFooter />
    </div>
  );
}

export default PricingAndPlans;
